import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FiUploadCloud, FiFileText, FiX, FiCheckCircle, FiClock, FiAlertTriangle } from 'react-icons/fi';
import { fetchLabUploadContext, uploadLabReports } from '../../Services/referralBusiness';

const MAX_BYTES = 25 * 1024 * 1024;

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      })
    : '—';

const fmtSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

const Shell = ({ children }) => (
  <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
    <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">{children}</div>
  </div>
);

export default function LabReportUpload() {
  const { token } = useParams();
  const fileInput = useRef(null);

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let active = true;
    fetchLabUploadContext(token)
      .then((data) => active && setContext(data))
      .catch((e) => active && setError(e?.response?.data?.message || 'This link is not valid any more'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const addFiles = (incoming) => {
    setFormError('');
    const picked = Array.from(incoming || []);

    const wrongType = picked.find((file) => file.type !== 'application/pdf');
    if (wrongType) return setFormError('Reports must be PDF files.');

    const tooBig = picked.find((file) => file.size > MAX_BYTES);
    if (tooBig) return setFormError(`"${tooBig.name}" is larger than the 25MB limit.`);

    setFiles((current) => [...current, ...picked].slice(0, 10));
  };

  const removeFile = (index) => setFiles((current) => current.filter((_, i) => i !== index));

  const submit = async () => {
    if (!files.length) return setFormError('Please choose at least one PDF report.');

    setUploading(true);
    setFormError('');
    try {
      const result = await uploadLabReports(token, files, notes);
      setDone(result?.message || 'Report uploaded. Thank you.');
    } catch (e) {
      setFormError(e?.response?.data?.message || 'Could not upload the report. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return <Shell><p className="text-center text-slate-500">Opening your secure link…</p></Shell>;
  }

  if (error) {
    return (
      <Shell>
        <div className="text-center">
          <FiAlertTriangle className="mx-auto h-10 w-10 text-amber-500" />
          <h1 className="mt-3 text-lg font-semibold text-slate-800">Link unavailable</h1>
          <p className="mt-2 text-sm text-slate-600">{error}</p>
          <p className="mt-4 text-xs text-slate-400">Please contact the clinic for a fresh upload link.</p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <FiCheckCircle className="mx-auto h-12 w-12 text-emerald-500" />
          <h1 className="mt-3 text-lg font-semibold text-slate-800">Report received</h1>
          <p className="mt-2 text-sm text-slate-600">{done}</p>
          <p className="mt-4 text-xs text-slate-400">
            This link has now been used and will not open again. You may close this window.
          </p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="border-b border-slate-100 pb-4">
        <p className="text-xs uppercase tracking-wider text-slate-400">{context?.clinicName}</p>
        <h1 className="mt-1 text-xl font-semibold text-slate-800">Upload pathology report</h1>
        <p className="mt-1 text-sm text-slate-600">
          For <strong>{context?.patientName}</strong>, referred to {context?.businessName}.
        </p>
      </header>

      <section className="mt-4 rounded-xl bg-slate-50 p-4">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Tests advised</p>
        <ul className="mt-2 space-y-1 text-sm text-slate-700">
          {(context?.tests || []).map((test) => (
            <li key={test} className="flex gap-2"><span className="text-slate-400">•</span>{test}</li>
          ))}
        </ul>
      </section>

      <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
        <FiClock className="h-3.5 w-3.5" />
        This link can be used once and expires on {fmtDateTime(context?.expiresAt)}.
      </p>

      <button
        type="button"
        onClick={() => fileInput.current?.click()}
        className="mt-5 flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-6 text-slate-500 hover:border-primary-400 hover:text-primary-600"
      >
        <FiUploadCloud className="h-8 w-8" />
        <span className="text-sm font-medium">Choose PDF report(s)</span>
        <span className="text-xs text-slate-400">PDF only, up to 25MB each</span>
      </button>
      <input
        ref={fileInput}
        type="file"
        accept="application/pdf"
        multiple
        className="hidden"
        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
      />

      {files.length ? (
        <ul className="mt-4 space-y-2">
          {files.map((file, index) => (
            <li key={`${file.name}-${index}`} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2">
              <FiFileText className="h-4 w-4 shrink-0 text-slate-400" />
              <span className="flex-1 truncate text-sm text-slate-700">{file.name}</span>
              <span className="text-xs text-slate-400">{fmtSize(file.size)}</span>
              <button type="button" onClick={() => removeFile(index)} className="text-slate-400 hover:text-rose-600">
                <FiX className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-4">
        <label className="mb-1 block text-xs font-medium text-slate-700">Notes for the clinic (optional)</label>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={500}
          className="w-full rounded-md border border-slate-300 p-2 text-sm focus:border-primary-500 focus:ring-primary-500"
          placeholder="Anything the doctor should know about this report"
        />
      </div>

      {formError ? <p className="mt-3 text-sm text-rose-600">{formError}</p> : null}

      <button
        type="button"
        disabled={uploading || !files.length}
        onClick={submit}
        className="mt-5 w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
      >
        {uploading ? 'Uploading…' : `Submit ${files.length || ''} report${files.length === 1 ? '' : 's'}`.trim()}
      </button>

      <p className="mt-4 text-center text-xs text-slate-400">
        Sent to you by {context?.clinicName}. Please do not forward this link.
      </p>
    </Shell>
  );
}
